import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip, ReferenceLine, BarChart, Bar, Cell,
} from "recharts";
import { Atom, BookOpen } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// HFO vs HFC blowing agents (2026 data, Honeywell Solstice / Chemours Opteon)
// PIR base λ values per blowing agent type
const BLOWING_AGENTS = {
  HFC: { name: "HFC-245fa", lambda_base: 0.024, gwp: 1030, labelKey: "pur.agent.hfc.label", labelEn: "Older generation — being phased out" },
  HFO: { name: "HFO-1233zd(E)", lambda_base: 0.0215, gwp: 1, labelKey: "pur.agent.hfo.label", labelEn: "New generation · low GWP" },
};

const PIR_BASE = {
  flexural: 250,            // kPa
  compressive: 180,         // kPa (ISO 844)
  density: 40,              // kg/m³
};

// BiCAT (Bismuth catalyst) effect per 1000 ppm — 2024 polyurethane research
const BICAT_COEFF = {
  compressive: +4.5,        // ↑ cell uniformity, ↑ compressive
  flexural: +3.2,
  lambda: -0.00008,         // tighter cell structure
  reaction_time: -3.0,      // faster cure
};

export default function PURModule() {
  const { t } = useI18n();
  const [agent, setAgent] = useState("HFO");
  const [bicat, setBicat] = useState(800);   // ppm

  const pred = useMemo(() => {
    const ba = BLOWING_AGENTS[agent];
    const lambda = ba.lambda_base + BICAT_COEFF.lambda * (bicat / 1000);
    const compressive = PIR_BASE.compressive + BICAT_COEFF.compressive * (bicat / 1000);
    const flexural = PIR_BASE.flexural + BICAT_COEFF.flexural * (bicat / 1000);
    return {
      lambda: Number(lambda.toFixed(5)),
      compressive: Math.round(compressive),
      flexural: Math.round(flexural),
      gwp: ba.gwp,
      reaction_time: Math.max(20, 60 + BICAT_COEFF.reaction_time * (bicat / 1000)),
    };
  }, [agent, bicat]);

  const lambdaChart = useMemo(() => {
    const out = [];
    for (let t = -20; t <= 60; t += 5) {
      const k_hfc = 0.024 + 0.00006 * (t - 23);
      const k_hfo = 0.0215 + 0.00004 * (t - 23);
      out.push({ T: t, HFC: Number(k_hfc.toFixed(5)), HFO: Number(k_hfo.toFixed(5)) });
    }
    return out;
  }, []);

  const compressData = useMemo(() => {
    const out = [];
    for (let p = 0; p <= 1500; p += 100) {
      const c = PIR_BASE.compressive + BICAT_COEFF.compressive * (p / 1000);
      out.push({ ppm: p, compressive: Math.round(c) });
    }
    return out;
  }, []);

  return (
    <section className="border-2 border-[#39FF14] bg-[#051F0A]" data-testid="pur-module">
      <header className="border-b border-[#22C55E] px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#22C55E' }}>
        <div className="flex items-center gap-2">
          <Atom className="w-4 h-4" style={{ color: '#FFFFFF' }}/>
          <span className="pur-title-emerald font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#FFFFFF', fontWeight: 700, opacity: 1, letterSpacing: '0.02em' }}>
            {t("pur.title", "PUR/PIR Module · HFO ↔ HFC Blowing Agent + BiCAT Catalyst")}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: '#FFFFFF', fontWeight: 700, opacity: 0.9 }}>ISO 844 · F-GAS REGULATION</span>
      </header>
      <div className="p-4 space-y-4">
        <p className="pur-desc font-mono text-[11px] leading-relaxed">
          {t("pur.desc", "Under the EU F-Gas regulation, high-GWP HFC blowing agents are being replaced with low-GWP HFO (Honeywell Solstice · Chemours Opteon). BiCAT (bismuth catalyst) is the tin-free, homogeneous-cell alternative to lead-based systems — it increases the ISO 844 compressive strength by 15-25%.")}
        </p>

        {/* Blowing agent selector */}
        <div className="grid grid-cols-2 gap-2" data-testid="agent-selector">
          {Object.entries(BLOWING_AGENTS).map(([key, ba]) => {
            const isActive = agent === key;
            return (
              <button key={key} onClick={() => setAgent(key)}
                className="p-3 text-left transition-all"
                style={{
                  backgroundColor: isActive ? '#22C55E' : '#DCFCE7',
                  color: isActive ? '#FFFFFF' : '#000000',
                  border: isActive ? '2px solid #166534' : '1px solid #86EFAC',
                  borderRadius: '6px',
                  boxShadow: isActive ? '0 0 8px rgba(34, 197, 94, 0.4)' : 'none',
                  fontWeight: isActive ? 700 : 600,
                  opacity: 1
                }}
                data-testid={`agent-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm" style={{ color: isActive ? '#FFFFFF' : '#000000', fontWeight: 700, opacity: 1 }}>
                    {ba.name}
                  </span>
                  <span className="font-mono text-[10px]" style={{ color: isActive ? '#FFFFFF' : '#166534', fontWeight: 600, opacity: 0.9 }}>GWP {ba.gwp}</span>
                </div>
                <div className="font-mono text-[10px] mt-1" style={{ color: isActive ? '#FFFFFF' : '#1A202C', fontWeight: 500, opacity: 0.85 }}>{t(ba.labelKey, ba.labelEn)}</div>
                <div className="font-mono text-[11px] mt-2" style={{ color: isActive ? '#FFFFFF' : '#166534', fontWeight: 700, opacity: 1 }}>
                  λ₀ = {ba.lambda_base} W/mK
                </div>
              </button>
            );
          })}
        </div>

        {/* BiCAT slider */}
        <div data-testid="bicat-slider">
          <div className="flex items-center justify-between mb-1" style={{ backgroundColor: '#1B5E20', padding: '10px 20px', borderRadius: '8px' }}>
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, opacity: 1 }}>
              {t("pur.slider", "BiCAT Bismuth Catalyst (ppm)")}
            </span>
            <span className="font-mono text-lg" style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 800, opacity: 1 }}>{bicat}</span>
          </div>
          <Slider value={[bicat]} min={0} max={1500} step={50}
            onValueChange={(v) => setBicat(v[0])} className="my-2"/>
          <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>0 (uncatalyzed) → 1500 ppm (premium PIR)</div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#1F1F1F] border border-[#1F1F1F]" data-testid="pur-results">
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>{t("pur.cell.lambda", "λ EFFECTIVE")}</div>
            <div className="font-mono text-xl" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 800, opacity: 1 }}>{pred.lambda}</div>
            <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>W/mK · 23°C</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>{t("pur.cell.compress", "COMPRESSIVE (ISO 844)")}</div>
            <div className="font-mono text-xl" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 800, opacity: 1 }}>{pred.compressive}</div>
            <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>kPa</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>{t("pur.cell.flexural", "FLEXURAL")}</div>
            <div className="font-mono text-xl" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 800, opacity: 1 }}>{pred.flexural}</div>
            <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>kPa</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>GWP</div>
            <div className="font-mono text-xl" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 800, opacity: 1 }}>
              {pred.gwp}
            </div>
            <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>CO₂eq</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>{t("pur.cell.reaction", "REACTION")}</div>
            <div className="font-mono text-xl" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 800, opacity: 1 }}>{pred.reaction_time.toFixed(0)}</div>
            <div className="font-mono text-[10px]" style={{ color: '#39FF14', WebkitTextFillColor: '#39FF14', fontWeight: 700, opacity: 1 }}>{t("pur.cell.reaction.unit", "seconds")}</div>
          </div>
        </div>

        {/* λ vs Temperature comparison */}
        <div className="rd-chart-container border border-[#1F1F1F] bg-[#050505] p-3" data-chart="true" data-testid="lambda-chart" style={{ backgroundColor: '#000000' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-wider neon-green">
              {t("pur.chart.lambda", "λ Temperature Dependence · HFC vs HFO")}
            </span>
            <span className="font-mono text-[10px] text-[#8B8B95]">−20°C → 60°C</span>
          </div>
          <div style={{ height: 200, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lambdaChart} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
                <CartesianGrid stroke="#1F1F1F"/>
                <XAxis dataKey="T" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}
                  label={{ value: "°C", position: "insideBottomRight", fill: "#8B8B95", fontSize: 10 }}/>
                <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}
                  domain={[0.020, 0.028]}/>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #39FF14",
                  fontFamily: "IBM Plex Mono", fontSize: 11, color: "#FFF" }}/>
                <Line type="linear" dataKey="HFC" stroke="#FF8C42" strokeWidth={2}
                  dot={false} name="HFC-245fa" isAnimationActive={false}/>
                <Line type="linear" dataKey="HFO" stroke="#39FF14" strokeWidth={2}
                  dot={false} name="HFO-1233zd" isAnimationActive={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Compressive vs BiCAT */}
        <div className="rd-chart-container border border-[#1F1F1F] bg-[#050505] p-3" data-chart="true" data-testid="compress-chart" style={{ backgroundColor: '#000000' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-wider neon-green">
              {t("pur.chart.compress", "ISO 844 Compressive Strength · BiCAT Loading")}
            </span>
            <span className="font-mono text-[10px] text-[#8B8B95]">0 → 1500 ppm</span>
          </div>
          <div style={{ height: 180, minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compressData} margin={{ top: 8, right: 8, bottom: 4, left: -16 }}>
                <CartesianGrid stroke="#1F1F1F"/>
                <XAxis dataKey="ppm" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}/>
                <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}/>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #39FF14",
                  fontFamily: "IBM Plex Mono", fontSize: 11, color: "#FFF" }}/>
                <ReferenceLine x={bicat} stroke="#FFFFFF" strokeDasharray="2 2"/>
                <Bar dataKey="compressive" fill="#39FF14" isAnimationActive={false}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <article className="border border-[#39FF14] bg-[#0A1F12] p-3" data-testid="prof-note-pur">
          <header className="flex items-center gap-2 mb-2">
            <BookOpen className="w-3.5 h-3.5 neon-green"/>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold neon-green">
              {t("pur.note.title", "Professor's Note · F-Gas Transition")}
            </span>
          </header>
          <p className="text-sm leading-relaxed text-white">
            {t("pur.note.body", "Effective 2026, the EU F-Gas regulation completely phases HFC blowing agents out of the PUR/PIR sector. HFO-1233zd(E) improves the λ value by 10% while reducing GWP from 1030 → 1. The BiCAT catalyst became critical with the REACH-driven exit of tin (Sn)-based legacy systems — its homogeneous cell structure raises ISO 844 compressive strength by +25%.")}
          </p>
        </article>
      </div>
    </section>
  );
}
